import React, { useCallback, useEffect, useRef, useState, memo } from 'react';
import ReactFlow, {
  Background, Controls, MiniMap,
  addEdge, useNodesState, useEdgesState,
  Handle, Position, MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import api from '../../utils/api';
import { toast } from 'sonner';
import {
  Play, Save, Eye, Trash2, Plus, GitBranch, Zap, Flag,
  ChevronDown, CheckCircle, X, Loader2, FolderOpen,
  RefreshCw, RotateCcw,
} from 'lucide-react';

// ─── Node Style Helpers ────────────────────────────────────────────────────

const nodeBase = {
  borderRadius: '16px',
  boxShadow: '0 4px 24px -4px rgba(0,0,0,0.12), 0 1px 0 rgba(255,255,255,0.8) inset',
  fontFamily: 'inherit',
  minWidth: '200px',
};

// ─── Custom Node: Start ───────────────────────────────────────────────────

const StartNode = memo(({ selected }) => (
  <div style={{
    ...nodeBase,
    background: 'linear-gradient(135deg,#10b981,#059669)',
    border: selected ? '2px solid #047857' : '2px solid #34d399',
    padding: '10px 24px',
    display: 'flex', alignItems: 'center', gap: '8px',
    color: 'white', fontWeight: 700, fontSize: '13px',
  }}>
    <Play size={14} />
    Start
    <Handle type="source" position={Position.Bottom} id="source"
      style={{ background: '#047857', border: '2px solid #d1fae5', width: 10, height: 10, bottom: -6 }} />
  </div>
));

// ─── Custom Node: Condition ──────────────────────────────────────────────

const ConditionNode = memo(({ data, selected }) => {
  const operatorLabel = { equals:'=', not_equals:'≠', gt:'>', lt:'<', gte:'≥', lte:'≤', is_true:'is ON', is_false:'is OFF', between:'between' };
  const isBetween = data.operator === 'between';
  const showValue = !['is_true','is_false'].includes(data.operator);
  return (
    <div style={{
      ...nodeBase,
      background: 'white',
      border: selected ? '2px solid #6366f1' : '2px solid #e0e7ff',
      padding: '12px 16px',
      paddingBottom: '24px',
    }}>
      <Handle type="target" position={Position.Top} id="target"
        style={{ background: '#6366f1', border: '2px solid #e0e7ff', width: 10, height: 10, top: -6 }} />

      <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:8 }}>
        <div style={{ width:24, height:24, borderRadius:8, background:'#eef2ff', display:'flex', alignItems:'center', justifyContent:'center' }}>
          <GitBranch size={12} color="#6366f1" />
        </div>
        <span style={{ fontSize:9, fontWeight:700, color:'#6366f1', textTransform:'uppercase', letterSpacing:'0.08em' }}>If Condition</span>
      </div>

      <div style={{ fontSize:12, fontWeight:600, color:'#1e293b', lineHeight:1.5 }}>
        <span style={{ color:'#475569' }}>{data.fieldLabel || data.field || 'Field'}</span>
        {' '}
        <span style={{ color:'#6366f1', fontWeight:700 }}>{operatorLabel[data.operator] || data.operator || 'op'}</span>
        {isBetween
          ? <><br /><span style={{ color:'#0f172a' }}>{data.valueFrom || '?'} <span style={{color:'#6366f1'}}>to</span> {data.valueTo || '?'}</span></>
          : showValue && <><br /><span style={{ color:'#0f172a' }}>{data.valueLabel || data.value || '…'}</span></>
        }
      </div>

      <Handle type="source" position={Position.Bottom} id="yes"
        style={{ left:'28%', background:'#10b981', border:'2px solid #d1fae5', width:10, height:10, bottom:-6 }} />
      <Handle type="source" position={Position.Bottom} id="no"
        style={{ left:'72%', background:'#ef4444', border:'2px solid #fee2e2', width:10, height:10, bottom:-6 }} />

      <div style={{ position:'absolute', bottom:4, left:0, right:0, display:'flex', justifyContent:'space-around', pointerEvents:'none' }}>
        <span style={{ fontSize:9, fontWeight:800, color:'#10b981' }}>YES</span>
        <span style={{ fontSize:9, fontWeight:800, color:'#ef4444' }}>NO</span>
      </div>
    </div>
  );
});

// ─── Custom Node: Action ─────────────────────────────────────────────────

const ACTION_COLORS = {
  setScholarshipPercent:   { bg:'#faf5ff', border:'#d8b4fe', icon:'#9333ea', text:'#7e22ce' },
  setSiblingDiscountTrue:  { bg:'#f0fdf4', border:'#86efac', icon:'#16a34a', text:'#15803d' },
  setSiblingDiscountFalse: { bg:'#fef2f2', border:'#fca5a5', icon:'#dc2626', text:'#b91c1c' },
  setTransportTrue:        { bg:'#f0f9ff', border:'#7dd3fc', icon:'#0284c7', text:'#0369a1' },
  setTransportFalse:       { bg:'#fef2f2', border:'#fca5a5', icon:'#dc2626', text:'#b91c1c' },
  setFamilyId:             { bg:'#fff7ed', border:'#fdba74', icon:'#ea580c', text:'#c2410c' },
};

const ActionNode = memo(({ data, selected }) => {
  const col = ACTION_COLORS[data.actionType] || { bg:'#f8fafc', border:'#e2e8f0', icon:'#64748b', text:'#475569' };
  return (
    <div style={{
      ...nodeBase,
      background: col.bg,
      border: selected ? `2px solid ${col.icon}` : `2px solid ${col.border}`,
      padding: '12px 16px',
    }}>
      <Handle type="target" position={Position.Top} id="target"
        style={{ background: col.icon, border: `2px solid ${col.border}`, width:10, height:10, top:-6 }} />

      <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:8 }}>
        <div style={{ width:24, height:24, borderRadius:8, background:`${col.icon}22`, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <Zap size={12} color={col.icon} />
        </div>
        <span style={{ fontSize:9, fontWeight:700, color:col.icon, textTransform:'uppercase', letterSpacing:'0.08em' }}>Then Action</span>
      </div>

      <div style={{ fontSize:12, fontWeight:600, color:col.text, lineHeight:1.5 }}>
        {data.actionLabel || data.actionType || 'Select action…'}
        {data.actionValue !== undefined && data.actionValue !== '' && !['setSiblingDiscountTrue','setSiblingDiscountFalse','setTransportTrue','setTransportFalse'].includes(data.actionType) && (
          <span style={{ color:'#0f172a', fontWeight:700 }}> → {data.actionValue}{data.actionType === 'setScholarshipPercent' ? '%' : ''}</span>
        )}
      </div>

      <Handle type="source" position={Position.Bottom} id="source"
        style={{ background: col.icon, border: `2px solid ${col.border}`, width:10, height:10, bottom:-6 }} />
    </div>
  );
});

// ─── Custom Node: End ─────────────────────────────────────────────────────

const EndNode = memo(({ selected }) => (
  <div style={{
    ...nodeBase,
    background: 'linear-gradient(135deg,#1e293b,#334155)',
    border: selected ? '2px solid #64748b' : '2px solid #475569',
    padding: '10px 24px',
    display:'flex', alignItems:'center', gap:'8px',
    color:'white', fontWeight:700, fontSize:'13px',
  }}>
    <Handle type="target" position={Position.Top} id="target"
      style={{ background:'#64748b', border:'2px solid #cbd5e1', width:10, height:10, top:-6 }} />
    <Flag size={14} />
    End
  </div>
));

// ─── Node Type Registry ───────────────────────────────────────────────────

const NODE_TYPES = { startNode: StartNode, conditionNode: ConditionNode, actionNode: ActionNode, endNode: EndNode };

// ─── Field / Operator / Action Config ─────────────────────────────────────

const FIELDS = [
  { value:'classGrade',             label:'Class Grade (number)' },
  { value:'classId',                label:'Specific Class' },
  { value:'hasFamilyId',            label:'Has Family ID (sibling)' },
  { value:'siblingDiscountApplied', label:'Has Sibling Discount' },
  { value:'scholarshipPercent',     label:'Scholarship %' },
  { value:'useTransport',           label:'Uses Transport' },
];

const OPERATORS_BY_FIELD = {
  classGrade:             ['between','equals','gt','lt','gte','lte'],
  classId:                ['equals','not_equals'],
  hasFamilyId:            ['is_true','is_false'],
  siblingDiscountApplied: ['is_true','is_false'],
  scholarshipPercent:     ['equals','not_equals','gt','lt','gte','lte'],
  useTransport:           ['is_true','is_false'],
};

const OPERATOR_LABELS = {
  equals:'equals', not_equals:'does not equal', gt:'greater than',
  lt:'less than', gte:'at least', lte:'at most',
  is_true:'is enabled', is_false:'is disabled',
  between:'is between',
};

const ACTION_TYPES = [
  { value:'setScholarshipPercent',   label:'Set Scholarship %', hasValue:true,  valuePlaceholder:'e.g. 20' },
  { value:'setSiblingDiscountTrue',  label:'Apply Sibling Discount', hasValue:false },
  { value:'setSiblingDiscountFalse', label:'Remove Sibling Discount', hasValue:false },
  { value:'setTransportTrue',        label:'Enable Transport', hasValue:false },
  { value:'setTransportFalse',       label:'Disable Transport', hasValue:false },
  { value:'setFamilyId',             label:'Set Family ID', hasValue:true, valuePlaceholder:'e.g. FAM-001' },
];

// ─── Main Page ────────────────────────────────────────────────────────────

let nodeCounter = 10;
const newId = () => `n${++nodeCounter}`;

export default function DiscountWorkflowPage() {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [workflowName, setWorkflowName] = useState('New Workflow');
  const [triggerOnEnrollment, setTriggerOnEnrollment] = useState(false);
  const [enrollmentSaving, setEnrollmentSaving] = useState(false);
  const [currentWorkflowId, setCurrentWorkflowId] = useState(null);
  const [savedWorkflows, setSavedWorkflows] = useState([]);
  const [classes, setClasses] = useState([]);
  const [previewResult, setPreviewResult] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showWorkflowList, setShowWorkflowList] = useState(false);
  const rfWrapper = useRef(null);

  useEffect(() => {
    fetchClasses();
    fetchWorkflows();
  }, []);

  const fetchClasses = async () => {
    try {
      const res = await api.get('/classes');
      setClasses(res.data);
    } catch (e) { /* silent */ }
  };

  const fetchWorkflows = async () => {
    try {
      const res = await api.get('/discount-workflows');
      setSavedWorkflows(res.data);
    } catch (e) { /* silent */ }
  };

  // ── Node addition ──────────────────────────────────────────────────────

  const addNode = (type) => {
    const centerX = (rfWrapper.current?.offsetWidth || 600) / 2 - 100;
    const y = 80 + nodes.length * 120;
    const id = newId();
    let data = {};
    if (type === 'conditionNode') data = { field: 'classGrade', fieldLabel: 'Class Grade (number)', operator: 'between', valueFrom: '', valueTo: '' };
    if (type === 'actionNode')    data = { actionType: 'setScholarshipPercent', actionLabel: 'Set Scholarship %', actionValue: '0' };

    setNodes(nds => [...nds, {
      id, type,
      position: { x: centerX + Math.random() * 40 - 20, y },
      data,
    }]);
  };

  // ── Connection ─────────────────────────────────────────────────────────

  const onConnect = useCallback((params) => {
    const isYes = params.sourceHandle === 'yes';
    const isNo  = params.sourceHandle === 'no';
    const edge = {
      ...params,
      id: `e-${params.source}-${params.sourceHandle || 'src'}-${params.target}`,
      label: isYes ? 'YES' : isNo ? 'NO' : '',
      labelStyle: { fontSize: 9, fontWeight: 800, fill: isYes ? '#10b981' : isNo ? '#ef4444' : '#94a3b8' },
      labelBgStyle: { fill: isYes ? '#f0fdf4' : isNo ? '#fef2f2' : '#f8fafc', fillOpacity: 0.9 },
      labelBgPadding: [4, 6],
      labelBgBorderRadius: 4,
      style: { stroke: isYes ? '#10b981' : isNo ? '#ef4444' : '#6366f1', strokeWidth: 2 },
      markerEnd: { type: MarkerType.ArrowClosed, color: isYes ? '#10b981' : isNo ? '#ef4444' : '#6366f1' },
      animated: true,
    };
    setEdges(eds => addEdge(edge, eds));
  }, [setEdges]);

  // ── Node selection ─────────────────────────────────────────────────────

  const onNodeClick = useCallback((_, node) => setSelectedNode(node), []);
  const onPaneClick = useCallback(() => setSelectedNode(null), []);

  // ── Update selected node data ──────────────────────────────────────────

  const patchNode = (patch) => {
    setNodes(nds => nds.map(n => n.id === selectedNode.id ? { ...n, data: { ...n.data, ...patch } } : n));
    setSelectedNode(prev => ({ ...prev, data: { ...prev.data, ...patch } }));
  };

  const deleteSelectedNode = () => {
    if (!selectedNode) return;
    setNodes(nds => nds.filter(n => n.id !== selectedNode.id));
    setEdges(eds => eds.filter(e => e.source !== selectedNode.id && e.target !== selectedNode.id));
    setSelectedNode(null);
  };

  // ── Save ───────────────────────────────────────────────────────────────

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { name: workflowName, nodes, edges, triggerOnEnrollment };
      if (currentWorkflowId) {
        await api.put(`/discount-workflows/${currentWorkflowId}`, payload);
        toast.success('Workflow saved');
      } else {
        const res = await api.post('/discount-workflows', payload);
        setCurrentWorkflowId(res.data.id);
        toast.success('Workflow created');
      }
      fetchWorkflows();
    } catch (e) {
      toast.error('Failed to save workflow');
    } finally {
      setSaving(false);
    }
  };

  // ── Auto-enroll toggle — saves immediately if workflow already exists ──

  const handleToggleEnrollment = async () => {
    const next = !triggerOnEnrollment;
    setTriggerOnEnrollment(next);

    if (!currentWorkflowId) return; // new unsaved workflow — state is enough, will save on Save click

    setEnrollmentSaving(true);
    try {
      await api.put(`/discount-workflows/${currentWorkflowId}`, {
        name: workflowName,
        nodes,
        edges,
        triggerOnEnrollment: next,
      });
      fetchWorkflows();
      toast.success(next ? 'Auto-enroll enabled — workflow will fire on new student enrollments' : 'Auto-enroll disabled');
    } catch (e) {
      setTriggerOnEnrollment(!next); // revert on error
      toast.error('Failed to update auto-enroll setting');
    } finally {
      setEnrollmentSaving(false);
    }
  };

  // ── Load ───────────────────────────────────────────────────────────────

  const loadWorkflow = async (wf) => {
    setNodes(wf.nodes || []);
    setEdges(wf.edges || []);
    setWorkflowName(wf.name);
    setTriggerOnEnrollment(wf.triggerOnEnrollment === true);
    setCurrentWorkflowId(wf.id);
    setSelectedNode(null);
    setPreviewResult(null);
    setShowWorkflowList(false);
    toast.success(`Loaded: ${wf.name}`);
  };

  const newWorkflow = () => {
    setNodes([]);
    setEdges([]);
    setWorkflowName('New Workflow');
    setTriggerOnEnrollment(false);
    setCurrentWorkflowId(null);
    setSelectedNode(null);
    setPreviewResult(null);
    setShowWorkflowList(false);
  };

  const deleteWorkflow = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/discount-workflows/${id}`);
      if (currentWorkflowId === id) newWorkflow();
      fetchWorkflows();
      toast.success('Workflow deleted');
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  // ── Preview ────────────────────────────────────────────────────────────

  const handlePreview = async () => {
    if (!currentWorkflowId) {
      toast.error('Save the workflow first before previewing');
      return;
    }
    // Auto-save first
    await handleSave();
    setPreviewLoading(true);
    setShowPreview(true);
    try {
      const res = await api.post(`/discount-workflows/${currentWorkflowId}/preview`);
      setPreviewResult(res.data);
    } catch (e) {
      toast.error('Preview failed');
      setShowPreview(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  // ── Apply ──────────────────────────────────────────────────────────────

  const handleApply = async (force = false) => {
    if (!currentWorkflowId) { toast.error('Save first'); return; }
    const count = previewResult?.affectedCount ?? '?';
    const msg = force
      ? `Force re-apply to ALL matching students? This will overwrite existing values for ${count} students.`
      : `Apply to ${count} new students?`;
    if (!window.confirm(msg)) return;
    setApplyLoading(true);
    try {
      const res = await api.post(`/discount-workflows/${currentWorkflowId}/apply${force ? '?force=true' : ''}`);
      toast.success(res.data.message);
      setShowPreview(false);
    } catch (e) {
      toast.error('Apply failed');
    } finally {
      setApplyLoading(false);
    }
  };

  const handleReset = async () => {
    if (!currentWorkflowId) return;
    if (!window.confirm('Reset tracking for this workflow? All students will be eligible to receive it again on next apply.')) return;
    setResetLoading(true);
    try {
      const res = await api.post(`/discount-workflows/${currentWorkflowId}/reset`);
      toast.success(res.data.message);
    } catch (e) {
      toast.error('Reset failed');
    } finally {
      setResetLoading(false);
    }
  };

  // ── Properties Panel ───────────────────────────────────────────────────

  const renderPropertiesPanel = () => {
    if (!selectedNode) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-center px-4 text-slate-400">
          <GitBranch className="w-10 h-10 mb-3 opacity-30" />
          <p className="text-xs font-semibold">Click any node<br />to edit its properties</p>
        </div>
      );
    }

    const d = selectedNode.data;

    return (
      <div className="space-y-4 p-4">
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
            {selectedNode.type === 'conditionNode' ? 'If Condition' :
             selectedNode.type === 'actionNode' ? 'Then Action' :
             selectedNode.type === 'startNode' ? 'Start' : 'End'}
          </span>
          <button onClick={deleteSelectedNode} className="p-1.5 text-rose-500 hover:bg-rose-50 rounded-lg transition-colors">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>

        {selectedNode.type === 'conditionNode' && (
          <>
            {/* Field */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Condition Field</label>
              <select
                value={d.field || ''}
                onChange={e => {
                  const f = FIELDS.find(x => x.value === e.target.value);
                  const ops = OPERATORS_BY_FIELD[e.target.value] || [];
                  const defaultOp = ops[0] || 'equals';
                  patchNode({ field: e.target.value, fieldLabel: f?.label, operator: defaultOp, value: '', valueLabel: '', valueFrom: '', valueTo: '' });
                }}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 text-xs outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400"
              >
                {FIELDS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </div>

            {/* Operator */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Operator</label>
              <select
                value={d.operator || ''}
                onChange={e => patchNode({ operator: e.target.value })}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 text-xs outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400"
              >
                {(OPERATORS_BY_FIELD[d.field] || ['equals']).map(op => (
                  <option key={op} value={op}>{OPERATOR_LABELS[op] || op}</option>
                ))}
              </select>
            </div>

            {/* Value inputs — hidden for boolean operators */}
            {!['is_true','is_false'].includes(d.operator) && (
              d.operator === 'between' ? (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">From grade</label>
                    <input type="number" min="1" max="12"
                      value={d.valueFrom || ''}
                      onChange={e => patchNode({ valueFrom: e.target.value })}
                      placeholder="e.g. 1"
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 text-xs outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">To grade</label>
                    <input type="number" min="1" max="12"
                      value={d.valueTo || ''}
                      onChange={e => patchNode({ valueTo: e.target.value })}
                      placeholder="e.g. 6"
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 text-xs outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400"
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Value</label>
                  {d.field === 'classId' ? (
                    <select
                      value={d.value || ''}
                      onChange={e => {
                        const cls = classes.find(c => c.id === e.target.value);
                        patchNode({ value: e.target.value, valueLabel: cls?.className });
                      }}
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 text-xs outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400"
                    >
                      <option value="">Select class…</option>
                      {classes.map(c => <option key={c.id} value={c.id}>{c.className}</option>)}
                    </select>
                  ) : (
                    <input
                      type={['classGrade','scholarshipPercent'].includes(d.field) ? 'number' : 'text'}
                      value={d.value || ''}
                      onChange={e => patchNode({ value: e.target.value, valueLabel: e.target.value })}
                      placeholder="Enter value…"
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 text-xs outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400"
                    />
                  )}
                </div>
              )
            )}

            <div className="p-3 bg-indigo-50/60 rounded-xl border border-indigo-100 text-[10px] text-indigo-700 font-medium">
              <span className="font-bold">If</span> {d.fieldLabel || d.field}{' '}
              <span className="font-bold">{OPERATOR_LABELS[d.operator] || d.operator}</span>
              {d.operator === 'between'
                ? <> <span className="font-bold">{d.valueFrom || '?'}</span> and <span className="font-bold">{d.valueTo || '?'}</span></>
                : !['is_true','is_false'].includes(d.operator) && <> <span className="font-bold">{d.valueLabel || d.value || '…'}</span></>
              }
              <br /><span className="text-indigo-400">→ follows YES or NO path</span>
            </div>
          </>
        )}

        {selectedNode.type === 'actionNode' && (
          <>
            {/* Action Type */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Action Type</label>
              <select
                value={d.actionType || ''}
                onChange={e => {
                  const a = ACTION_TYPES.find(x => x.value === e.target.value);
                  patchNode({ actionType: e.target.value, actionLabel: a?.label, actionValue: a?.hasValue ? d.actionValue : '' });
                }}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 text-xs outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400"
              >
                {ACTION_TYPES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
            </div>

            {/* Action Value (conditional) */}
            {ACTION_TYPES.find(a => a.value === d.actionType)?.hasValue && (
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Value</label>
                <input
                  type={d.actionType === 'setScholarshipPercent' ? 'number' : 'text'}
                  min={d.actionType === 'setScholarshipPercent' ? 0 : undefined}
                  max={d.actionType === 'setScholarshipPercent' ? 100 : undefined}
                  value={d.actionValue || ''}
                  onChange={e => patchNode({ actionValue: e.target.value })}
                  placeholder={ACTION_TYPES.find(a => a.value === d.actionType)?.valuePlaceholder || ''}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 text-xs outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400"
                />
              </div>
            )}

            <div className="p-3 bg-purple-50/60 rounded-xl border border-purple-100 text-[10px] text-purple-700 font-medium">
              <span className="font-bold">Then:</span> {d.actionLabel || d.actionType}
              {d.actionValue !== '' && ACTION_TYPES.find(a => a.value === d.actionType)?.hasValue && (
                <> → <span className="font-bold">{d.actionValue}{d.actionType === 'setScholarshipPercent' ? '%' : ''}</span></>
              )}
            </div>
          </>
        )}

        {(selectedNode.type === 'startNode' || selectedNode.type === 'endNode') && (
          <p className="text-xs text-slate-400 text-center">No properties to configure.</p>
        )}
      </div>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 100px)' }}>

      {/* Top Toolbar */}
      <div className="flex items-center justify-between gap-4 mb-4">

        {/* Left: title + editable workflow name */}
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center shrink-0">
            <GitBranch className="w-5 h-5 text-indigo-600" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <input
                value={workflowName}
                onChange={e => setWorkflowName(e.target.value)}
                className="text-xl font-bold text-slate-800 bg-transparent outline-none border-b-2 border-transparent focus:border-indigo-300 transition-colors w-56 leading-tight"
                placeholder="Workflow name"
              />
              {currentWorkflowId
                ? <span className="text-[10px] font-semibold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full shrink-0">saved</span>
                : <span className="text-[10px] font-semibold text-amber-500 bg-amber-50 px-2 py-0.5 rounded-full shrink-0">unsaved</span>
              }
            </div>
            <p className="text-xs text-slate-400 mt-0.5">Visual if/else logic for fees, discounts &amp; transport</p>
          </div>
        </div>

        {/* Right: controls */}
        <div className="flex items-center gap-2 shrink-0">

          {/* Auto-enroll pill toggle — saves immediately */}
          <button
            onClick={handleToggleEnrollment}
            disabled={enrollmentSaving}
            title={currentWorkflowId
              ? 'When ON, this workflow runs automatically for every new student enrolled'
              : 'Save the workflow first to enable auto-enroll'}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold transition-all border disabled:opacity-70 ${
              triggerOnEnrollment
                ? 'bg-emerald-500 border-emerald-500 text-white shadow-sm shadow-emerald-200'
                : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-700'
            }`}
          >
            {enrollmentSaving
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <Zap className="w-3.5 h-3.5" />
            }
            Auto-enroll
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${triggerOnEnrollment ? 'bg-emerald-400 text-white' : 'bg-slate-100 text-slate-500'}`}>
              {triggerOnEnrollment ? 'ON' : 'OFF'}
            </span>
          </button>

          <div className="w-px h-6 bg-slate-200" />

          {/* Load */}
          <div className="relative">
            <button onClick={() => setShowWorkflowList(v => !v)}
              className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-full text-sm font-medium transition-colors">
              <FolderOpen className="w-4 h-4" />
              Load
              <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
            </button>
            {showWorkflowList && (
              <div className="absolute right-0 top-full mt-1.5 bg-white border border-slate-200 rounded-2xl shadow-xl z-50 min-w-[220px] overflow-hidden">
                <div className="p-2">
                  <button onClick={newWorkflow}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-indigo-600 hover:bg-indigo-50 rounded-xl transition-colors">
                    <Plus className="w-3.5 h-3.5" /> New Workflow
                  </button>
                </div>
                {savedWorkflows.length > 0 && (
                  <div className="border-t border-slate-100 p-2 space-y-0.5 max-h-52 overflow-y-auto">
                    {savedWorkflows.map(wf => (
                      <div key={wf.id}
                        onClick={() => loadWorkflow(wf)}
                        className={`flex items-center justify-between px-3 py-2 rounded-xl cursor-pointer transition-colors ${currentWorkflowId === wf.id ? 'bg-indigo-50 text-indigo-700' : 'hover:bg-slate-50 text-slate-700'}`}>
                        <span className="text-xs font-semibold truncate">{wf.name}</span>
                        <button onClick={e => deleteWorkflow(wf.id, e)} className="ml-2 p-0.5 text-rose-400 hover:text-rose-600 shrink-0">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {savedWorkflows.length === 0 && <p className="px-4 pb-3 text-[10px] text-slate-400">No saved workflows yet</p>}
              </div>
            )}
          </div>

          {/* Save */}
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-1.5 px-5 py-2 bg-slate-900 hover:bg-slate-700 text-white rounded-full text-sm font-medium transition-colors disabled:opacity-50 shadow-sm">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>

          {/* Preview */}
          <button onClick={handlePreview} disabled={previewLoading}
            className="flex items-center gap-1.5 px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full text-sm font-medium transition-colors disabled:opacity-50 shadow-sm">
            {previewLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
            Preview
          </button>
        </div>
      </div>

      {/* Main 3-column layout */}
      <div className="flex flex-1 gap-3 min-h-0">

        {/* Left: Node Palette */}
        <div className="w-44 shrink-0 bg-white rounded-2xl border border-slate-200/60 shadow-sm flex flex-col overflow-hidden">
          <div className="px-3 py-2.5 border-b border-slate-100">
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Node Palette</p>
          </div>
          <div className="p-2 space-y-1.5 overflow-y-auto flex-1">
            {[
              { type:'startNode', icon:Play, label:'Start', color:'#10b981', bg:'#f0fdf4', border:'#bbf7d0' },
              { type:'conditionNode', icon:GitBranch, label:'If / Condition', color:'#6366f1', bg:'#eef2ff', border:'#c7d2fe' },
              { type:'actionNode', icon:Zap, label:'Then / Action', color:'#9333ea', bg:'#faf5ff', border:'#d8b4fe' },
              { type:'endNode', icon:Flag, label:'End', color:'#64748b', bg:'#f8fafc', border:'#e2e8f0' },
            ].map(({ type, icon:Icon, label, color, bg, border }) => (
              <button
                key={type}
                onClick={() => addNode(type)}
                style={{ borderColor: border, background: bg }}
                className="w-full flex items-center gap-2 px-2.5 py-2 rounded-xl border text-left hover:scale-[1.02] active:scale-[0.98] transition-transform"
              >
                <div style={{ background: `${color}22`, borderRadius: 8, width: 26, height: 26, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <Icon size={13} color={color} />
                </div>
                <span style={{ fontSize:11, fontWeight:600, color:'#1e293b' }}>{label}</span>
              </button>
            ))}

            <div className="pt-2 border-t border-slate-100 mt-2">
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">How to use</p>
              {[
                'Click a node to add it',
                'Drag nodes to arrange',
                'Connect handles by dragging',
                'Click a node to edit it',
                'Condition → YES/NO paths',
              ].map((tip, i) => (
                <p key={i} className="text-[9px] text-slate-400 mb-0.5">• {tip}</p>
              ))}
            </div>
          </div>
        </div>

        {/* Center: Canvas */}
        <div ref={rfWrapper} className="flex-1 bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden relative">
          {nodes.length === 0 && (
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-10">
              <GitBranch className="w-12 h-12 text-slate-200 mb-3" />
              <p className="text-sm font-semibold text-slate-300">Add nodes from the left panel</p>
              <p className="text-xs text-slate-200 mt-1">Start → Condition → Action → End</p>
            </div>
          )}
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            nodeTypes={NODE_TYPES}
            fitView
            proOptions={{ hideAttribution: true }}
            defaultEdgeOptions={{
              style: { stroke: '#6366f1', strokeWidth: 2 },
              markerEnd: { type: MarkerType.ArrowClosed, color: '#6366f1' },
              animated: true,
            }}
          >
            <Background color="#e2e8f0" gap={20} size={1} />
            <Controls style={{ bottom: 16, left: 16 }} />
            <MiniMap
              nodeColor={(n) =>
                n.type === 'startNode' ? '#10b981' :
                n.type === 'conditionNode' ? '#6366f1' :
                n.type === 'actionNode' ? '#9333ea' : '#64748b'
              }
              style={{ borderRadius: 12, overflow:'hidden', border:'1px solid #e2e8f0' }}
            />
          </ReactFlow>
        </div>

        {/* Right: Properties Panel */}
        <div className="w-56 shrink-0 bg-white rounded-2xl border border-slate-200/60 shadow-sm flex flex-col overflow-hidden">
          <div className="px-3 py-2.5 border-b border-slate-100">
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Properties</p>
          </div>
          <div className="flex-1 overflow-y-auto">
            {renderPropertiesPanel()}
          </div>
        </div>
      </div>

      {/* Preview Panel */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-end justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[70vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div>
                <h3 className="font-bold text-slate-800 flex items-center gap-2">
                  <Eye className="w-4 h-4 text-indigo-600" /> Workflow Preview
                </h3>
                {previewResult && (
                  <div className="flex items-center gap-3 mt-1">
                    <p className="text-xs text-slate-500">
                      <span className="font-bold text-indigo-600">{previewResult.affectedCount}</span>
                      <span className="text-slate-400"> new students</span>
                    </p>
                    {previewResult.alreadyProcessedCount > 0 && (
                      <p className="text-xs text-slate-400">
                        <span className="font-semibold text-amber-500">{previewResult.alreadyProcessedCount}</span> already applied (skipped)
                      </p>
                    )}
                    {previewResult.lastAppliedAt && (
                      <p className="text-[10px] text-slate-400">
                        Last run: {new Date(previewResult.lastAppliedAt).toLocaleString()}
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                {previewResult?.alreadyProcessedCount > 0 && (
                  <button
                    onClick={() => handleApply(true)}
                    disabled={applyLoading}
                    title="Force re-apply to all matching students, including those already processed"
                    className="flex items-center gap-1.5 px-3 py-2 border border-amber-200 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-full text-xs font-bold transition-colors disabled:opacity-50">
                    <RefreshCw className="w-3.5 h-3.5" />
                    Force Re-apply
                  </button>
                )}
                {previewResult?.affectedCount > 0 && (
                  <button onClick={() => handleApply(false)} disabled={applyLoading}
                    className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full text-xs font-bold transition-colors disabled:opacity-50">
                    {applyLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                    Apply to New Students
                  </button>
                )}
                {previewResult?.affectedCount === 0 && previewResult?.alreadyProcessedCount > 0 && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 rounded-full border border-emerald-200">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                    <span className="text-xs font-semibold text-emerald-700">All students already processed</span>
                  </div>
                )}
                <button
                  onClick={handleReset}
                  disabled={resetLoading || !currentWorkflowId}
                  title="Clear tracking so all students can be re-processed"
                  className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors text-slate-400 hover:text-amber-500"
                >
                  {resetLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                </button>
                <button onClick={() => setShowPreview(false)} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors">
                  <X className="w-4 h-4 text-slate-500" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {previewLoading ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
                </div>
              ) : previewResult?.results?.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-slate-400">
                  <CheckCircle className="w-8 h-8 mb-2 opacity-40" />
                  <p className="text-sm font-semibold">
                    {previewResult?.alreadyProcessedCount > 0
                      ? 'All matching students already have this workflow applied'
                      : 'No students match this workflow'}
                  </p>
                  {previewResult?.alreadyProcessedCount > 0 && (
                    <p className="text-xs mt-1">Use Force Re-apply or Reset to run it again</p>
                  )}
                </div>
              ) : (
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      <th className="px-4 py-2.5 text-[9px] font-black text-slate-400 uppercase tracking-widest">Student</th>
                      <th className="px-4 py-2.5 text-[9px] font-black text-slate-400 uppercase tracking-widest">Class</th>
                      <th className="px-4 py-2.5 text-[9px] font-black text-slate-400 uppercase tracking-widest">Changes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewResult?.results?.map((r) => (
                      <tr key={r.studentId} className="border-b border-slate-50 hover:bg-slate-50/50">
                        <td className="px-4 py-3">
                          <p className="text-sm font-semibold text-slate-800">{r.name}</p>
                          <p className="text-[10px] text-slate-400">{r.admissionNumber}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-slate-600">{r.className}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {r.descriptions.map((d, i) => (
                              <span key={i} className="inline-block px-2 py-0.5 bg-indigo-50 text-indigo-700 text-[10px] font-semibold rounded-full border border-indigo-100">
                                {d}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
